import React, { useState, useEffect, useContext } from "react";
import { Redirect } from "react-router-dom";
import { Grid, Button, Typography, Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import CustomerDocumentIcon from "@material-ui/icons/FolderShared";

import { createCustomerDocument, getCustomerDocument, updateCustomerDocument, getDocumentUrl } from "../api/DocumentAPI";
import translate from "../i18n/Translator";
import { Document, DocumentRequest } from "../model/Document";
import Progress from "../components/Progress";
import { ErrorSnackbar } from "../components/Snackbars";
import Surface from "../components/Surface";
import ValidatedInput, { InputRef } from "../components/ValidatedInput";
import { AppContext } from "../context/AppContext";
import { RouterParams } from "../router/RouterParams";
import UploaderInput from "../components/UploaderInput";

export default function DocumentForm({ match }: RouterParams) {
    const context = useContext(AppContext);
    const theme = useTheme();
    const isXs = useMediaQuery(() => theme.breakpoints.down("xs"));
    const isEdit = !!match.params.documentId;
    const margin = "dense";

    const [status, setStatus] = useState<string>("loading");
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [redirect, setRedirect] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [url, setUrl] = useState<string>();
    const [request, setRequest] = useState<DocumentRequest>({
        name: ""
    } as DocumentRequest);
    const [validations, setValidations] = useState({
        "name": {
            valid: false
        } as InputRef
    } as any);

    const submitPromise = (): Promise<Document> => {
        if (isEdit && match.params.documentId) {
            return updateCustomerDocument(context.session!.tenant!.id, match.params.documentId, request);
        }
        return createCustomerDocument(context.session!.tenant!.id, request);
    };

    useEffect(() => {
        setStatus("loading");
        if (isEdit) {
            getCustomerDocument(context.session!.tenant!.id, match.params.documentId).then((customerdocument) => {
                setRequest({
                    name: customerdocument.name,
                    description: customerdocument.description
                } as DocumentRequest);
                setStatus("loaded");

                getDocumentUrl(context.session!.tenant!.id, match.params.documentId).then(url => {
                    setUrl(url);
                }).catch(error => {

                });
            }).catch((error) => {
                setStatus(error.message);
            });
        } else {
            setStatus("loaded");
        }
        // eslint-disable-next-line
    }, [isEdit, match.params.documentId]);

    const hasChanged = (name: string, value: string, inputRef: InputRef) => {
        setRequest({ ...request, [name]: value });
        validations[name] = inputRef;
        setValidations(validations);
    };

    const isValid = () => {
        var valid = true;
        for (let field in validations) {
            let ref = validations[field];
            if (!ref.valid) {
                ref.blurer(true);
                valid = false;
            }
        }
        return valid;
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValid()) {
            return;
        }

        setSubmitting(true);
        submitPromise().then((response) => {
            setRedirect(true);
        }).catch((error) => {
            setError(error.message);
            setSubmitting(false);
        })
    };

    const onClosedSnackbar = () => {
        setError(undefined);
    };

    if (redirect) {
        return <Redirect to="/documents" />
    }

    if (status === "loading") {
        return <Progress />;
    }

    if (status !== "loaded") {
        return (
            <Typography variant="body1" component="h5" color="error" align="center">
                {status}
            </Typography>
        );
    }

    return (
        <Grid item xs={12}>
            <Grid container justify="center" alignItems="center">
                <Grid item xs={12} md={10} lg={7} xl={5}>
                    <Surface title={translate(isEdit ? "documents.edit" : "documents.new")} icon={<CustomerDocumentIcon />} backButton className="FormSurface">
                        <form autoComplete="off" noValidate onSubmit={onSubmit} >
                            <Grid container justify="space-between" alignItems="center">
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="name" name="name"
                                        value={request.name} label={translate("documents.name") as string}
                                        required={true} disabled={submitting} maxLength={100}
                                        margin={margin}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ValidatedInput type="text" id="description" name="description"
                                        value={request.description} label={translate("documents.description") as string}
                                        required disabled={submitting} maxLength={255}
                                        margin={margin} maxRows={3} rows={3}
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <UploaderInput id="url" name="url"
                                        value={request.url || url} label={translate("documents.file") as string}
                                        required={!isEdit} disabled={submitting}
                                        margin={margin} acceptExtension=".pdf,.doc,.docx"
                                        onValueChanged={hasChanged} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box pt={2}>
                                        <Grid container justify="flex-start" spacing={1} direction="row-reverse">
                                            <Grid item xs sm="auto">
                                                <Button type="submit" variant="contained" color="primary" size="large" disabled={submitting} fullWidth={isXs}>
                                                    {translate(isEdit ? "buttons.update" : "buttons.add")}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>
                        <ErrorSnackbar message={error} onClose={onClosedSnackbar} />
                    </Surface>
                </Grid>
            </Grid>
        </Grid>
    );
}